/**
 * Browser binding that lags a value by one painted frame: `useAfterPaint(value)` returns the value
 * as it was when the last frame was painted, and catches up in a background-priority render a
 * frame after the next paint. Views use it to show the visible part of a change in the frame that answers the
 * input and leave off-screen or secondary work to the render that follows. It holds no decisions;
 * the frame and task scheduling is `lib/afterPaint`.
 *
 * @module apps/web/src/hooks/useAfterPaint
 */

import { useEffect, useState } from 'react';
import { afterNextPaint } from '@/lib/afterPaint';

/**
 * Return `value` once a frame has been painted after it changed; until then return the previous
 * value. The catch-up is queued two animation frames later as a `background` priority task
 * (`afterNextPaint` with `frames: 2`), so it runs after that frame's paint and after any
 * user-visible task already queued, rather than before the paint (React's own deferred renders
 * may run before the paint and delay it). Values compare with `Object.is`; pass primitives or
 * stable references. Safe for any component; a pending catch-up is cancelled on change or unmount.
 */
export function useAfterPaint<T>(value: T): T {
  const [painted, setPainted] = useState(value);
  useEffect(() => {
    if (Object.is(painted, value)) return;
    return afterNextPaint(() => setPainted(value), { frames: 2 });
  }, [value, painted]);
  return painted;
}
